import {
  MEDIA_TYPE,
  type Participant,
  useConferenceStore,
  useConfigStore,
  useIsParticipantPinned,
  useRaisedHandTimestamp,
  useTrackMuted,
} from '@cometchat/calls-sdk-core';
import React from 'react';
import Avatar from '../../avatar/Avatar.web';
import Icon from '../../icon/Icon.web';
import MoreOptionButton from './MoreOptionButton.web';
import './style.css';

interface ParticipantItemProps {
  participant: Participant;
}

const QUALITY_COLOR_MAP: Record<number, string> = {
  1: 'var(--cometchat-calls-error-color)', // Poor - Red
  2: 'var(--cometchat-calls-warning-color)', // Fair - Orange
  3: 'var(--cometchat-calls-success-color)', // Good - Green
  4: 'var(--cometchat-calls-success-color)', // Excellent - Green
};

const ConnectionQualityIndicator = ({
  participantId,
}: {
  participantId: Participant['pid'];
}) => {
  const connectionQuality = useConferenceStore(state =>
    state.connectionQuality.get(participantId),
  );
  const hideNetworkIndicator = useConfigStore(
    state => state.hideNetworkIndicator,
  );
  if (hideNetworkIndicator || connectionQuality === undefined) {
    return null;
  }
  const quality = (connectionQuality >> 5) + 1;
  const signalColor = QUALITY_COLOR_MAP[quality];
  return <Icon name="signal" size={24} fill={signalColor} />;
};

const ParticipantItem: React.FC<ParticipantItemProps> = ({ participant }) => {
  const { pid, name, avatar, type, ruid } = participant;
  const description = participant.type === 'screen-share' ? 'Presentation' : '';
  const muted = useTrackMuted(MEDIA_TYPE.AUDIO, pid);
  const micIconName = muted ? 'mic-off' : 'mic';
  const micIconColor = muted
    ? 'var(--cometchat-calls-icon-color-secondary)'
    : 'var(--cometchat-calls-primary-color)';

  // Check if participant has raised hand
  const raisedHandTimestamp = useRaisedHandTimestamp(pid, type);
  const isPinnedParticipant = useIsParticipantPinned(
    participant?.pid ?? '',
    participant?.type ?? 'human',
  );

  return (
    <div className="cometchat-calls-participant-item">
      <div className="cometchat-calls-participant-avatar">
        <Avatar name={name} imageUrl={avatar} fontSize={20} />
        {isPinnedParticipant && (
          <div className="cometchat-calls-participant-pin-icon">
            <Icon
              name="pin"
              size={15}
              fill="var(--cometchat-calls-static-white)"
            />
          </div>
        )}
      </div>
      <div className="cometchat-calls-participant-text">
        <h4 className="cometchat-calls-participant-name">{name}</h4>
        {description && (
          <p className="cometchat-calls-participant-description">
            {description}
          </p>
        )}
      </div>
      <div className="cometchat-calls-participant-icons">
        {raisedHandTimestamp && type === 'human' && (
          <Icon
            name="raise-hand-fill"
            size={22}
            fill="var(--cometchat-calls-static-white)"
          />
        )}
        {type === 'screen-share' && (
          <Icon
            name="screen-share"
            size={24}
            fill="var(--cometchat-calls-primary-color)"
          />
        )}
        <ConnectionQualityIndicator participantId={pid} />
        <Icon name={micIconName} size={24} fill={micIconColor} />
        <MoreOptionButton ruid={ruid} />
      </div>
    </div>
  );
};

export default ParticipantItem;
